<template>
  <div class="bg-[--header-bottom-bg-color] text-[--header-bottom-text-color]">
    <div class="relative mx-auto w-full max-w-screen-2xl px-8 md:px-12 bg-inherit">
      <nav ref="bottomHeader" class="relative z-[2] flex min-h-[5rem] items-center gap-x-5 bg-inherit">
        <router-link :to="$context.settings.default_return_url ?? '/'">
          <VcImage :src="logoUrl" :alt="$context.storeName" class="h-6" sync />
        </router-link>

        <!-- Innovadis custom: no organization name display -->

        <div class="flex flex-row self-end mr-5">
          <!-- Catalog button -->
          <a
            v-if="catalogMenuItems.length"
            ref="showCatalogMenuButton"
            :href="catalogLink"
            type="button"
            class="flex select-none border-[--color-body-bg] items-center border-b-4 px-0 ml-4 pt-[0.55rem] pb-6 text-lg text-[--color-body-text] hover:text-[--color-primary-500]"
            :class="{ 'border-[--color-primary-500]': catalogMenuVisible }"
            @click="toggleCatalogDropdown"
            @keydown.enter="toggleCatalogDropdown"
            @keydown.space="toggleCatalogDropdown"
            @keydown.esc="closeCatalogDropdown"
          >
            <span class="font-bold tracking-wide">
              {{ $t("shared.layout.header.bottom_header.catalog_menu_button") }}
            </span>
            <VcIcon :name="catalogButtonIcon" size="xs" class="ml-1.5 mt-[5px] fill-neutral flex items-center" />
          </a>
          <span
            v-else
            class="flex select-none border-[--color-body-bg] items-center border-b-4 px-0 ml-4 pt-[0.55rem] pb-6 text-lg text-[--color-additional-300]"
          >
            <span class="font-bold tracking-wide animate-letters">
              <span
                v-for="(letter, index) in animatedLetters"
                :key="index"
                class="inline-block opacity-0 animate-fade"
                :style="{ animationDelay: `${index * 100}ms` }"
              >
                {{ letter }}
              </span>
            </span>
          </span>

          <!-- Brands button -->
          <router-link :to="{ name: 'Brands' }">
            <div
              class="flex select-none border-[--color-body-bg] items-center border-b-4 px-0 ml-4 pt-[0.55rem] pb-6 text-lg text-[--color-body-text] hover:text-[--color-primary-500]"
              :class="{ 'border-[--color-primary-500]': isBrandPage }"
            >
              <span class="font-bold tracking-wide">
                {{ $t("shared.layout.header.bottom_header.brands_menu_button") }}
              </span>
            </div>
          </router-link>
        </div>

        <InnoSearchBar />

        <ul class="flex items-center xl:ml-40">
          <li v-for="item in desktopMainMenuItems" :key="item.id" class="flex flex-row justify-center">
            <InnoBottomHeaderLink
              v-if="item.id === 'login' && !isAuthenticated"
              :link="item"
              :aria-label="$t('shared.account.navigation.links.login')"
              class="w-auto px-2"
              ><span class="pl-1 font-semibold">{{ $t("shared.account.navigation.links.login") }}</span>
            </InnoBottomHeaderLink>

            <InnoBottomHeaderLink
              v-else-if="item.id === 'login' && isAuthenticated"
              :link="{ ...item, route: '/account/dashboard' }"
              :aria-label="$t('shared.account.navigation.links.dashboard')"
              class="w-auto px-2"
              ><span
                data-test-id="main-layout.bottom-header.customer-name-label"
                class="pl-1"
                v-html-safe="
                  sanitizeText(
                    $t('shared.account.navigation.links.welcome', { name: user.contact?.firstName || user.userName }),
                  )
                "
              ></span>
            </InnoBottomHeaderLink>

            <InnoBottomHeaderLink
              v-else-if="item.id === 'cart'"
              :link="item"
              :aria-label="$t('shared.account.navigation.links.login')"
              class="w-auto px-2"
              :count="cart?.itemsQuantity"
            />

            <InnoBottomHeaderLink v-else :link="item" :aria-label="item.title" />
          </li>
        </ul>
      </nav>

      <!-- Catalog dropdown -->
      <transition
        v-if="catalogMenuItems.length"
        enter-from-class="-translate-y-full"
        leave-to-class="-translate-y-full"
        enter-active-class="will-change-transform"
        leave-active-class="will-change-transform"
      >
        <div
          v-if="catalogMenuVisible"
          ref="catalogMenuElement"
          class="absolute w-full overflow-y-auto pb-3 bg-inherit shadow-md transition-transform duration-200"
          :style="catalogMenuStyle"
        >
          <InnoCatalogMenu
            :items="catalogMenuItems"
            @focusout="focusoutCatalogDropdown"
            @close="closeCatalogDropdown"
            @select="closeCatalogDropdown"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import DOMPurify from "dompurify";
import { onClickOutside, useElementBounding } from "@vueuse/core";
import { computed, nextTick, ref, shallowRef, watch } from "vue";
import { useRoute } from "vue-router";
import { useNavigations, useWhiteLabeling } from "@/core/composables";
import { useUser } from "@/shared/account/composables/useUser";
import { InnoSearchBar } from "@/shared/layout";
import InnoCatalogMenu from "./inno-catalog-menu.vue";
import InnoBottomHeaderLink from "./inno-bottom-header-link.vue";
import type { StyleValue } from "vue";
import { useShortCart } from "@/shared/cart";
import { useI18n } from "vue-i18n";

const { cart } = useShortCart();

const route = useRoute();

const { t } = useI18n();

const { isAuthenticated, user } = useUser();
const { logoUrl } = useWhiteLabeling();
const { catalogMenuItems, desktopMainMenuItems } = useNavigations();

const bottomHeader = ref<HTMLElement | null>(null);
const { bottom } = useElementBounding(bottomHeader);

const catalogMenuElement = shallowRef<HTMLElement | null>(null);
const showCatalogMenuButton = shallowRef<HTMLElement | null>(null);
const catalogMenuVisible = ref(false);
const catalogButtonIcon = computed<string>(() =>
  catalogMenuVisible.value ? "chevron-bottom-header-up" : "chevron-bottom-header-down",
);
const catalogMenuStyle = computed<StyleValue | undefined>(() =>
  bottom.value ? { maxHeight: `calc(100vh - ${bottom.value}px)` } : undefined,
);
const catalogLink = "#"; // Innovadis custom: no link on catalog button, only dropdown -- router.resolve({ name: "Catalog" }).fullPath;
const catalogMenuText = computed(() => t("shared.layout.header.bottom_header.catalog_menu_button"));
const animatedLetters = computed(() => {
  return catalogMenuText.value.split("");
});

const isBrandPage = computed(() => route.path === "/brands");

function sanitizeText(input: string) {
  return DOMPurify.sanitize(input as string, {
    USE_PROFILES: { html: true },
  });
}

onClickOutside(
  catalogMenuElement,
  () => {
    catalogMenuVisible.value = false;
  },
  { ignore: [showCatalogMenuButton] },
);

// Removed this line to prevent the body scroll from disappearing when the dropdown is active.
// This prevents page jumps.
// syncRefs(catalogMenuVisible, useScrollLock(document.body));

async function toggleCatalogDropdown(event: Event) {
  if (!catalogMenuItems.value.length) {
    return;
  }
  event.preventDefault();
  catalogMenuVisible.value = !catalogMenuVisible.value;
  await nextTick();
  if (catalogMenuVisible.value) {
    catalogMenuElement.value?.querySelector("a")?.focus();
  }
}

async function closeCatalogDropdown() {
  catalogMenuVisible.value = false;
  await nextTick();
  showCatalogMenuButton.value?.focus();
}

function focusoutCatalogDropdown(payload: FocusEvent) {
  if (payload.relatedTarget !== showCatalogMenuButton.value) {
    void closeCatalogDropdown();
  }
}

watch(route, () => {
  catalogMenuVisible.value = false;
});
</script>
